// Path: C:\gestion-copro\src\pages\admin-saas\ApporteurDetailPage.tsx
// Session 198 : Fiche complète d'un apporteur d'affaires
// Accessible depuis CreerApporteurModal → bouton "Voir la fiche"
// Route : /admin-saas/apporteur/:userId
// Session 207 : Commissions branchées — liaison manuelle prospect ↔ user, commission récurrente réelle

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Shield,
  TrendingUp,
  Users,
  Banknote,
  CheckCircle2,
  Clock,
  XCircle,
  Edit2,
  Save,
  X,
  Loader2,
  AlertTriangle,
  BarChart3,
  FileText,
  Calendar,
  ToggleLeft,
  ToggleRight,
  StickyNote,
  Link2,
  Link2Off,
  Euro,
} from 'lucide-react';
import { UserProfile } from '../../types/user.types';
import {
  Prospect,
  Commission,
  LABELS_STATUT_COMMISSION,
  LABELS_STATUT_PROSPECT,
  calculerCommissionMensuelle,
  calculerCAHTMensuel,
  TAUX_COMMISSION,
} from '../../types/crm.types';
import {
  chargerProspectsParResponsable,
  chargerCommissions,
  lierProspectUserManuellement,
  delierProspectUser,
} from '../../services/crm.service';

// ============================================================
// TYPES LOCAUX
// ============================================================

interface StatsPipeline {
  total: number;
  convertis: number;
  en_cours: number;
  perdus: number;
  tauxConversion: number;
  dernierProspect?: Timestamp;
}

// ============================================================
// HELPERS
// ============================================================

const formatDate = (ts?: Timestamp): string => {
  if (!ts) return '—';
  return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatEuros = (n: number): string =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const trimestreLabel = (t: string): string => {
  // Format: "2026-Q1"
  const [year, q] = t.split('-');
  return `${q} ${year}`;
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================

const ApporteurDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  // --- État principal ---
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Prospects ---
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loadingProspects, setLoadingProspects] = useState(true);

  // --- Commissions ---
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loadingCommissions, setLoadingCommissions] = useState(true);

  // --- Édition infos ---
  const [editMode, setEditMode] = useState(false);
  const [editNom, setEditNom] = useState('');
  const [editPrenom, setEditPrenom] = useState('');
  const [editTelephone, setEditTelephone] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCommission, setEditCommission] = useState(25);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // --- Toggle actif/inactif ---
  const [togglingStatus, setTogglingStatus] = useState(false);

  // --- Liaison manuelle prospect ↔ user ---
  const [prospectALier, setProspectALier] = useState<Prospect | null>(null);
  const [userIdLiaison, setUserIdLiaison] = useState('');
  const [liaisonEnCours, setLiaisonEnCours] = useState(false);
  const [liaisonErreur, setLiaisonErreur] = useState<string | null>(null);

  // ============================================================
  // CHARGEMENT
  // ============================================================

  const chargerUser = useCallback(async () => {
    if (!userId) return;
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) {
        setError('Utilisateur introuvable.');
        return;
      }
      const data = snap.data() as UserProfile;
      setUser({ ...data, id: snap.id });
      setEditNom(data.nom || '');
      setEditPrenom(data.prenom || '');
      setEditTelephone(data.apporteurInfo?.telephone || '');
      setEditNotes((data as any).notesInternes || '');
      setEditCommission(Math.round((data.apporteurInfo?.commission || 0.25) * 100));
    } catch (err) {
      console.error('[ApporteurDetail] Erreur chargement user:', err);
      setError('Erreur lors du chargement.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const chargerProspects = useCallback(async (identifiant: string) => {
    try {
      const data = await chargerProspectsParResponsable(identifiant);
      setProspects(data);
    } catch (err) {
      console.error('[ApporteurDetail] Erreur prospects:', err);
    } finally {
      setLoadingProspects(false);
    }
  }, []);

  const chargerCommissionsApporteur = useCallback(async (identifiant: string) => {
    try {
      const data = await chargerCommissions(identifiant);
      setCommissions(data);
    } catch (err) {
      console.error('[ApporteurDetail] Erreur commissions:', err);
    } finally {
      setLoadingCommissions(false);
    }
  }, []);

  useEffect(() => {
    chargerUser();
  }, [chargerUser]);

  useEffect(() => {
    if (!user?.apporteurInfo?.identifiant) return;
    const id = user.apporteurInfo.identifiant;
    chargerProspects(id);
    chargerCommissionsApporteur(id);
  }, [user, chargerProspects, chargerCommissionsApporteur]);

  // ============================================================
  // STATS PIPELINE
  // ============================================================

  const stats: StatsPipeline = React.useMemo(() => {
    const total = prospects.length;
    const convertis = prospects.filter((p) => p.statut === 'converti').length;
    const perdus = prospects.filter((p) => p.statut === 'perdu').length;
    const en_cours = total - convertis - perdus;
    const tauxConversion = total > 0 ? Math.round((convertis / total) * 100) : 0;
    const sorted = [...prospects].sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    );
    return { total, convertis, perdus, en_cours, tauxConversion, dernierProspect: sorted[0]?.createdAt };
  }, [prospects]);

  // Commissions agrégées
  const totalDu = commissions.reduce((s, c) => s + (c.resteAVerser || 0), 0);
  const totalVerse = commissions.reduce((s, c) => s + (c.montantVerse || 0), 0);

  // ============================================================
  // ACTIONS
  // ============================================================

  const handleSave = async () => {
    if (!user || !userId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', userId), {
        nom: editNom.trim(),
        prenom: editPrenom.trim(),
        'apporteurInfo.telephone': editTelephone.trim(),
        'apporteurInfo.commission': editCommission / 100,
        notesInternes: editNotes.trim(),
        updatedAt: Timestamp.now(),
      });
      setUser((prev) =>
        prev
          ? {
              ...prev,
              nom: editNom.trim(),
              prenom: editPrenom.trim(),
              apporteurInfo: prev.apporteurInfo
                ? {
                    ...prev.apporteurInfo,
                    telephone: editTelephone.trim(),
                    commission: editCommission / 100,
                  }
                : undefined,
            }
          : prev
      );
      setEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[ApporteurDetail] Erreur sauvegarde:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user || !userId) return;
    setTogglingStatus(true);
    try {
      const newStatus = user.status === 'active' ? 'rejected' : 'active';
      await updateDoc(doc(db, 'users', userId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });
      setUser((prev) => (prev ? { ...prev, status: newStatus } : prev));
    } catch (err) {
      console.error('[ApporteurDetail] Erreur toggle status:', err);
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleLierProspect = async () => {
    if (!prospectALier || !userIdLiaison.trim()) return;
    setLiaisonEnCours(true);
    setLiaisonErreur(null);
    const result = await lierProspectUserManuellement(prospectALier.id, userIdLiaison.trim());
    if (result.succes) {
      // Rafraîchir la liste des prospects
      if (user?.apporteurInfo?.identifiant) {
        const fresh = await chargerProspectsParResponsable(user.apporteurInfo.identifiant);
        setProspects(fresh);
      }
      setProspectALier(null);
      setUserIdLiaison('');
    } else {
      setLiaisonErreur(result.erreur || 'Erreur inconnue.');
    }
    setLiaisonEnCours(false);
  };

  const handleDelierProspect = async (prospectId: string) => {
    await delierProspectUser(prospectId);
    if (user?.apporteurInfo?.identifiant) {
      const fresh = await chargerProspectsParResponsable(user.apporteurInfo.identifiant);
      setProspects(fresh);
    }
  };

  // ============================================================
  // RENDER — ÉTATS
  // ============================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle size={40} className="text-red-400 mx-auto mb-3" />
        <p className="text-red-600 font-medium">{error || 'Apporteur introuvable.'}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-sm text-blue-600 hover:underline"
        >
          ← Retour
        </button>
      </div>
    );
  }

  const identifiant = user.apporteurInfo?.identifiant || '—';
  const isActif = user.status === 'active';
  const commissionPct = Math.round((user.apporteurInfo?.commission || 0.25) * 100);

  // ============================================================
  // RENDER PRINCIPAL
  // ============================================================

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">

      {/* ======= EN-TÊTE ======= */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {user.prenom} {user.nom}
          </h1>
          <p className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
            <span className="font-mono text-blue-600">@{identifiant}</span>
            <span>·</span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                isActif
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {isActif ? (
                <><CheckCircle2 size={11} /> Actif</>
              ) : (
                <><XCircle size={11} /> Désactivé</>
              )}
            </span>
            {user.role === 'vigie' && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                VIGIE + Apporteur
              </span>
            )}
          </p>
        </div>

        {/* Actions en-tête */}
        <div className="flex items-center gap-2">
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
              <CheckCircle2 size={15} /> Enregistré
            </span>
          )}
          {editMode ? (
            <>
              <button
                onClick={() => { setEditMode(false); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={15} /> Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Enregistrer
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border transition-colors"
            >
              <Edit2 size={15} /> Modifier
            </button>
          )}
        </div>
      </div>

      {/* ======= GRILLE PRINCIPALE ======= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ===== COLONNE GAUCHE : Infos + Contrôle ===== */}
        <div className="space-y-4">

          {/* Carte infos */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <User size={14} /> Informations
            </h2>

            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Prénom</label>
                    <input
                      type="text"
                      value={editPrenom}
                      onChange={(e) => setEditPrenom(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Nom</label>
                    <input
                      type="text"
                      value={editNom}
                      onChange={(e) => setEditNom(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={editTelephone}
                    onChange={(e) => setEditTelephone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Taux de commission (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={editCommission}
                    onChange={(e) => setEditCommission(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Standard Alain : 25%</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="break-all">{user.email}</span>
                </div>
                {user.apporteurInfo?.telephone && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone size={14} className="text-gray-400 flex-shrink-0" />
                    <span>{user.apporteurInfo.telephone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-700">
                  <Shield size={14} className="text-gray-400 flex-shrink-0" />
                  <span>Commission : <strong>{commissionPct}%</strong> du CA HT</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                  <span>Depuis le {formatDate(user.apporteurInfo?.dateDebut)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes internes */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <StickyNote size={14} /> Notes internes
            </h2>
            {editMode ? (
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={4}
                placeholder="Accord verbal du 15/03, spécialité associations nationales..."
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
              />
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {(user as any).notesInternes || (
                  <span className="text-gray-400 italic">Aucune note.</span>
                )}
              </p>
            )}
          </div>

          {/* Activation / Désactivation */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield size={14} /> Compte
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {isActif ? 'Compte actif' : 'Compte désactivé'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isActif
                    ? "L'apporteur peut accéder à son espace."
                    : "L'accès est suspendu."}
                </p>
              </div>
              <button
                onClick={handleToggleStatus}
                disabled={togglingStatus}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActif
                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                    : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                }`}
              >
                {togglingStatus ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : isActif ? (
                  <><ToggleRight size={16} /> Désactiver</>
                ) : (
                  <><ToggleLeft size={16} /> Réactiver</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ===== COLONNE DROITE : Stats + Prospects + Commissions ===== */}
        <div className="lg:col-span-2 space-y-6">

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: 'Prospects',
                value: stats.total,
                icon: <Users size={16} className="text-blue-500" />,
                color: 'bg-blue-50',
              },
              {
                label: 'Convertis',
                value: stats.convertis,
                icon: <CheckCircle2 size={16} className="text-green-500" />,
                color: 'bg-green-50',
              },
              {
                label: 'Taux conv.',
                value: `${stats.tauxConversion}%`,
                icon: <TrendingUp size={16} className="text-purple-500" />,
                color: 'bg-purple-50',
              },
              {
                label: 'À verser',
                value: formatEuros(totalDu),
                icon: <Banknote size={16} className="text-amber-500" />,
                color: 'bg-amber-50',
              },
            ].map((kpi) => (
              <div key={kpi.label} className={`${kpi.color} rounded-xl p-4 border border-white`}>
                <div className="flex items-center gap-1.5 mb-1">
                  {kpi.icon}
                  <span className="text-xs text-gray-500 font-medium">{kpi.label}</span>
                </div>
                <p className="text-xl font-bold text-gray-800">{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Commission récurrente branchée */}
          {(() => {
            const convertisLies = prospects.filter(
              (p) => p.statut === 'converti' && p.clientUserId && p.formuleClient
            );
            const convertisNonLies = prospects.filter(
              (p) => p.statut === 'converti' && !p.clientUserId
            );
            const commissionMensuelle = convertisLies.reduce(
              (sum, p) => sum + calculerCommissionMensuelle(p.formuleClient || ''),
              0
            );
            const caHTMensuel = convertisLies.reduce(
              (sum, p) => sum + calculerCAHTMensuel(p.formuleClient || ''),
              0
            );
            if (convertisLies.length === 0 && convertisNonLies.length === 0) return null;
            return (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <Euro size={14} /> Commission récurrente branchée
                  </h2>
                  {commissionMensuelle > 0 && (
                    <span className="text-sm font-bold text-blue-700">
                      {formatEuros(commissionMensuelle)} / mois
                    </span>
                  )}
                </div>

                {convertisLies.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {convertisLies.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-green-50 rounded-lg border border-green-200">
                        <Link2 size={14} className="text-green-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{p.nom}</p>
                          <p className="text-xs text-gray-500">{p.clientEmail || p.contactEmail || '—'} · {p.formuleClient || '—'}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-gray-400">Commission/mois</p>
                          <p className="text-sm font-bold text-blue-600">
                            {formatEuros(calculerCommissionMensuelle(p.formuleClient || ''))}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDelierProspect(p.id)}
                          title="Délier ce compte"
                          className="ml-1 p-1 text-gray-300 hover:text-red-400 transition-colors"
                        >
                          <Link2Off size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {convertisNonLies.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-600 font-semibold mb-1">
                      {convertisNonLies.length} converti{convertisNonLies.length > 1 ? 's' : ''} sans compte lié
                    </p>
                    {convertisNonLies.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-200">
                        <Link2Off size={14} className="text-amber-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{p.nom}</p>
                          <p className="text-xs text-gray-500">{p.contactEmail || '—'}</p>
                        </div>
                        <button
                          onClick={() => { setProspectALier(p); setUserIdLiaison(''); setLiaisonErreur(null); }}
                          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded-lg hover:bg-amber-50 transition-colors"
                        >
                          <Link2 size={12} /> Lier un compte
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {convertisLies.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>CA HT mensuel : <strong>{formatEuros(caHTMensuel)}</strong></span>
                    <span>Taux : <strong>{Math.round(TAUX_COMMISSION * 100)}%</strong></span>
                    <span>Annuel estimé : <strong className="text-blue-700">{formatEuros(commissionMensuelle * 12)}</strong></span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Prospects assignés */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <BarChart3 size={14} /> Prospects assignés
            </h2>
            {loadingProspects ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                <Loader2 size={14} className="animate-spin" /> Chargement...
              </div>
            ) : prospects.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4">Aucun prospect assigné.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {prospects.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{p.nom}</p>
                      <p className="text-xs text-gray-500">
                        {p.contactEmail || '—'} · ajouté le {formatDate(p.createdAt)}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                        p.statut === 'converti'
                          ? 'bg-green-100 text-green-700'
                          : p.statut === 'perdu'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {LABELS_STATUT_PROSPECT[p.statut]}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Commissions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Banknote size={14} /> Commissions
              </h2>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500">
                  Versé : <strong className="text-green-600">{formatEuros(totalVerse)}</strong>
                </span>
                <span className="text-gray-500">
                  Reste : <strong className="text-amber-600">{formatEuros(totalDu)}</strong>
                </span>
              </div>
            </div>

            {loadingCommissions ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                <Loader2 size={14} className="animate-spin" /> Chargement...
              </div>
            ) : commissions.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4">
                Aucune commission enregistrée.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {commissions.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-gray-50"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {trimestreLabel(c.trimestre)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {c.details?.length || 0} client(s) ·{' '}
                        {c.dateVersement ? `Versé le ${formatDate(c.dateVersement)}` : 'Non versé'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-800">
                        {formatEuros(c.montantDu)}
                      </p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          c.statut === 'verse'
                            ? 'bg-green-100 text-green-700'
                            : c.statut === 'partiel'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {LABELS_STATUT_COMMISSION[c.statut]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modale liaison manuelle */}
      {prospectALier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Link2 size={18} className="text-blue-600" />
                <h3 className="font-bold text-gray-800">Lier un compte utilisateur</h3>
              </div>
              <button onClick={() => setProspectALier(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-lg px-4 py-3 text-sm text-blue-700">
                Prospect : <strong>{prospectALier.nom}</strong>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  User ID Firestore
                </label>
                <input
                  type="text"
                  value={userIdLiaison}
                  onChange={(e) => setUserIdLiaison(e.target.value)}
                  placeholder="Ex : abc123xyz..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Visible dans Firestore → users → [ID du document] ou dans le panneau Utilisateurs VIGIE.
                </p>
              </div>
              {liaisonErreur && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                  <AlertTriangle size={14} className="shrink-0" />
                  {liaisonErreur}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 pb-5">
              <button
                onClick={() => setProspectALier(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleLierProspect}
                disabled={!userIdLiaison.trim() || liaisonEnCours}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {liaisonEnCours ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                Confirmer la liaison
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApporteurDetailPage;
